import { useInfiniteQuery } from '@tanstack/react-query';

import {
  CATALOG_ENTITY_REGISTRY,
  extractCatalogEntityModels,
  invokeCatalogListApi,
} from '@/adapters/shared/catalog/catalog-entity-registry';
import type { CatalogEntityId } from '@/types/data/catalog';
import type { Page, PageRequest } from '@/types/shared/page';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { catalogQueryKeys } from './query-keys';

/**
 * Generic hook for fetching any catalog entity type with cursor-based pagination.
 */
export function useListGenericCatalogEntities(
  tenantId: UUID | null,
  connectorId: UUID | null,
  entityId: CatalogEntityId,
  query?: PageRequest
) {
  const entityDef = CATALOG_ENTITY_REGISTRY[entityId];

  // `useCurrentTenantID()` resolves to the nil UUID while the tenant query is
  // still loading. The nil UUID is a non-empty string, so an `enabled: !!tenantId`
  // gate would treat it as a real tenant and fire a request against
  // /tenants/00000000-…, which the backend answers with a 500. The retry
  // middleware then hammers that 5xx, draining the shared client rate-limit
  // bucket; once empty, the limiter short-circuits later requests with a
  // synthetic 429 (no network call) — which is why the real list request
  // intermittently never appears in the network inspector. Gate on a real
  // tenant/connector (mirrors useFindConnector) so we fetch exactly once.
  const hasTarget = !isEmptyUUID(tenantId) && !isEmptyUUID(connectorId);

  return useInfiniteQuery<Page<unknown>, Error>({
    queryKey: catalogQueryKeys.genericEntityList(
      tenantId,
      connectorId,
      entityId,
      query ? { limit: query.limit, filter: query.filter } : undefined
    ),
    queryFn: async ({ pageParam }) => {
      const res = await invokeCatalogListApi(
        entityDef.apiMethod,
        tenantId!,
        connectorId!,
        { ...query, next: pageParam as string | undefined }
      );

      // Surface failed/blocked responses (5xx, auth, rate-limiter 429) as
      // errors so React Query retries them. Swallowing a `data`-less response
      // as an empty page caches a "no records" result that never recovers.
      if (!res?.ok) {
        throw new Error(
          `Failed to list ${entityDef.label} (status ${res?.status ?? 'unknown'})`
        );
      }

      const page = (res.data ?? {}) as {
        models?: unknown[];
        next?: string;
        total?: number;
      };
      const rawModels = (page.models ?? []) as Record<string, unknown>[];
      const items = extractCatalogEntityModels(entityId, rawModels);

      return { items, next: page.next ?? null, total: page.total };
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    enabled: hasTarget && !!entityDef,
  });
}

/** Safety cap on pages fetched by {@link fetchAllGenericCatalogEntities}. */
const EXPORT_MAX_PAGES = 1000;
/** Page size used when paging through every record for an export. */
const EXPORT_PAGE_SIZE = 100;

/**
 * Pages through every record of a catalog entity by following the `next` cursor,
 * returning the full flattened list. Used for "export all records" flows; it talks
 * to the API directly (reusing the same extraction logic as
 * {@link useListGenericCatalogEntities}) so it does not touch the React Query cache.
 */
export async function fetchAllGenericCatalogEntities(
  tenantId: UUID | null,
  connectorId: UUID | null,
  entityId: CatalogEntityId
): Promise<unknown[]> {
  const entityDef = CATALOG_ENTITY_REGISTRY[entityId];
  if (isEmptyUUID(tenantId) || isEmptyUUID(connectorId) || !entityDef) {
    return [];
  }

  const all: unknown[] = [];
  let next: string | undefined;

  for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
    const res = await invokeCatalogListApi(
      entityDef.apiMethod,
      tenantId!,
      connectorId!,
      {
        limit: EXPORT_PAGE_SIZE,
        next,
      }
    );

    if (!res?.ok) {
      throw new Error(
        `Failed to list ${entityDef.label} (status ${res?.status ?? 'unknown'})`
      );
    }

    const data = (res.data ?? {}) as { models?: unknown[]; next?: string };
    const rawModels = (data.models ?? []) as Record<string, unknown>[];
    all.push(...extractCatalogEntityModels(entityId, rawModels));

    next = data.next ?? undefined;
    if (!next) {
      break;
    }
  }

  return all;
}
